import { Injectable } from '@angular/core';
import { ApolloQueryResult } from 'apollo-client';
import { gql } from 'graphql-tag';
// import * as zlib from 'zlib';
import * as pako from 'pako';
import { Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { GetFilteredDataQuery } from 'src/API';
import { getFilteredData } from 'src/graphql/queries';
import { FilterType, GetFilteredDataQueryVariables } from './../../API';
import { AppSyncService } from './../core/appsync.service';
import { ClinicSetupService } from './../logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { FilterFieldSchema, FilterGroup } from './filter.model';

@Injectable({
  providedIn: 'root'
})
export class FilterService {
  dataGroupFilterFieldMap: FilterFieldSchema = null;

  public filterTypeCachedFilterMap: {
    [k in FilterType]?: { data: { [key: string]: any }; currentFetchForAll$?: Observable<any> };
  } = {
    patient: { data: {} },
    assessment: { data: {} },
    referral: { data: {} },
    doctor: { data: {} },
    intakeForm: { data: {} },
    questionnaireRequest: { data: {} }
  };

  public lowerKeyMap = {};

  constructor(
    private appsyncService: AppSyncService,
    private clinicSetupService: ClinicSetupService
  ) {}

  queryFilterData(type: FilterType, filter: FilterGroup[]): Observable<any[]> {
    const fetchAllData = Object.keys(this.filterTypeCachedFilterMap[type].data).length <= 0;

    let filterQuery$ = this.appsyncService.hydrated().pipe(
      switchMap(client =>
        client.query({
          query: gql(getFilteredData),
          variables: {
            type,
            filter: JSON.stringify({ fetch: fetchAllData ? 'all' : 'updates' }),
            clinicId: this.clinicSetupService.clinicId
          } as GetFilteredDataQueryVariables,
          fetchPolicy: 'network-only'
        })
      ),
      map((result: ApolloQueryResult<GetFilteredDataQuery>) =>
        JSON.parse(
          pako.inflate(JSON.parse(result.data.getFilteredData).data, {
            to: 'string'
          })
        )
      )
    );

    if (fetchAllData) {
      filterQuery$ = this.filterTypeCachedFilterMap[type].currentFetchForAll$ =
        this.filterTypeCachedFilterMap[type].currentFetchForAll$ || filterQuery$;
    }

    return filterQuery$.pipe(
      map((data: any[]) => {
        data.forEach(item => {
          if (item && item.id) {
            this.filterTypeCachedFilterMap[type].data[item.id] = item;
          } else {
            console.log('ERROR', item);
          }
        });

        if (filter) {
          return this.filterData(
            [...Object.values(this.filterTypeCachedFilterMap[type].data)],
            filter
          );
        }

        return [...Object.values(this.filterTypeCachedFilterMap[type].data)];
      }),
      shareReplay()
    );
  }

  filterData(dataItems, filter) {
    return dataItems.filter(item => {
      const e = this.evaluateFilter(item, filter);
      return e;
    });
  }

  evaluateFilter(item, filter) {
    if (!filter || !filter.length) {
      return true;
    }

    const stack = [...filter];
    let filtersMatched = false;
    while (stack.length > 0) {
      const filterGroup = stack.pop();

      if (filterGroup.field) {
        const partialKey = filterGroup.field.toLowerCase();
        filtersMatched = Object.keys(item).some(itemKey => {
          this.lowerKeyMap[itemKey] = this.lowerKeyMap[itemKey] || itemKey.toLowerCase();
          return (
            this.lowerKeyMap[itemKey].endsWith(partialKey) &&
            this.evaluateCondition(item[itemKey], filterGroup)
          );
        });
        return filtersMatched;
      } else if (filterGroup.type && filterGroup.filters) {
        // For "and", all sub-filters must match
        // For "or", at least one sub-filter must match
        let subFiltersMatch = false;

        for (const subFilter of filterGroup.filters) {
          if (this.evaluateFilter(item, [subFilter])) {
            subFiltersMatch = true;
            if (filterGroup.type === 'or') {
              break; // Exit early for "or"
            }
          } else if (filterGroup.type === 'and') {
            subFiltersMatch = false; // Reset for "and"
            break;
          }
        }

        if (subFiltersMatch) {
          return true;
        }
      }
    }
    return false;
  }

  evaluateCondition(actualValue, condition) {
    try {
      if (!actualValue) {
        return false;
      }

      let { operator, searchValue } = condition;
      searchValue = searchValue.trim();

      if (
        ['greaterThan', 'lessThan', 'greaterThanOrEqualTo', 'lessThanOrEqualTo'].includes(operator)
      ) {
        actualValue = new Date(actualValue).getTime() || parseInt(actualValue);
        searchValue = new Date(searchValue).getTime() || parseInt(searchValue);

        if (!actualValue || !searchValue) {
          return false;
        }
      }

      switch (operator) {
        case 'equals':
          return actualValue === searchValue;
        case 'contains':
          return actualValue.includes(searchValue);
        case 'notEqual':
          return actualValue !== searchValue;
        case 'notContains':
          return !actualValue.includes(searchValue);
        case 'startsWith':
          return actualValue.startsWith(searchValue);
        case 'endsWith':
          return actualValue.endsWith(searchValue);
        case 'greaterThan':
          return actualValue > searchValue;
        case 'lessThan':
          return actualValue < searchValue;
        case 'greaterThanOrEqualTo':
          return actualValue >= searchValue;
        case 'lessThanOrEqualTo':
          return actualValue <= searchValue;
        default:
          console.error(`Unsupported operator: ${operator}`);
          return false;
      }
    } catch (e) {
      console.log('Error', e);
      return false;
    }
  }
}
